import {
	Card,
	Change,
	MetricTile,
	PageHeader,
	SegmentedTab,
	SegmentedTabList,
	SegmentedTabs,
	Surface
} from '@clickguard/design-system'
import { RuleStatus, RuleTemplateVisibility } from 'api-models'
import { Lock01, Shield01, ShieldZap, SlashOctagon } from 'components/icons'
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from 'react-query'
import { toast } from 'react-toastify'
import { Navigate } from 'react-router-dom'
import { useFeatureFlag, useFeatureFlagsStatus } from 'services/feature-flags'
import { RuleTemplates } from 'services/user/rule-templates'
import { Rules } from 'services/user/rules'
import { useAccountStore, useAssetStore, useUserStore } from 'store'
// Module constants so there is exactly one answer per direction (see countries.ts).
import {
	countryCodeByName,
	countryNameByCode
} from 'components/country-select/countries'
import { PageTitle } from 'utils/page-title'
import { useTrackPostHogEvent } from 'utils/posthog'
import { PostHogEvent, PostHogPath } from 'utils/posthog-events'
import { mapFetchedRules } from '../protection/components/rules-panel/components/rules-list/utils'
import { CustomRulesTable } from './components/custom-rules-table'
import { CustomRulesPlanLocked } from './components/plan-locked'
import { useFadingScrollbar } from 'components/fading-scroll/fading-scrollbar'
import {
	RuleActivityProvider,
	RuleActivityWindow,
	activityRange,
	buildRuleActivity,
	ruleWindowTotals
} from './components/rule-activity'
import { toCustomRule } from './components/rule-adapters'
import { NEW_MODEL_ID_COLUMNS, prepareRuleData } from './components/rule-writer'
import { useRuleEntities } from './components/rule-entities'
import {
	ruleToTemplateConfig,
	toRuleTemplate
} from './components/template-adapters'
import {
	MAX_CHART_RULES,
	RulesActivityChart
} from './components/rules-activity-chart'
// No `swapDelay`: the four tiles fire together, so a stagger would only be lag on
// the last one. The table's rows keep theirs.
import { SwapValue } from './components/swap-value'
import {
	CustomRule,
	RuleDraft,
	RuleTemplate as RuleTemplateUI,
	TemplateScope
} from './components/types'
import 'components/page-surface/page-surface.css'
import './custom-rules-page.css'

/** 240px is the cap at which all three KPI captions settle into two lines. */
const KPI_INFO_WIDTH = 240

/** Time frames offered by the header switcher, in days — widest window first. */
const TIME_FRAMES = [30, 7] as const
type TimeFrame = (typeof TIME_FRAMES)[number]

/** The frame the page opens on. Named once, because the counters are seeded from
 *  it as well as the switcher. */
const INITIAL_TIME_FRAME: TimeFrame = 7

/** Re-reads every rule's counters for a time frame, so the KPI totals and the
 *  table's columns are the SAME numbers cut the same way. */
const applyTimeFrame = (
	rules: CustomRule[],
	days: TimeFrame,
	window: RuleActivityWindow
): CustomRule[] =>
	rules.map((rule) => {
		const totals = ruleWindowTotals(window, rule.id, days)
		return { ...rule, triggered: totals.triggered, blocked: totals.blocked }
	})

/** KPI totals from the fetched rules — same figures the table columns sum to. */
const summarize = (
	rules: CustomRule[],
	days: TimeFrame,
	window: RuleActivityWindow
) => {
	const executions = rules.reduce((sum, rule) => sum + rule.triggered, 0)
	const blocked = rules.reduce((sum, rule) => sum + rule.blocked, 0)
	// Measured over the rules on the page NOW: a deleted rule leaves both halves.
	const previous = rules.reduce(
		(sums, rule) => {
			const before = ruleWindowTotals(window, rule.id, days).previous
			return {
				executions: sums.executions + before.triggered,
				blocked: sums.blocked + before.blocked
			}
		},
		{ executions: 0, blocked: 0 }
	)
	// Counted among the ACTIVE rules on purpose. "Protective" means the rule shuts the
	// source out rather than labelling the click and letting it run.
	const active = rules.filter((rule) => rule.status === 'active')
	const protective = active.filter(
		(rule) =>
			rule.actions.blacklist ||
			rule.actions.tempIpExclusion ||
			rule.actions.audienceExclusion
	)
	return {
		executions,
		blocked,
		previous,
		protectiveRules: protective.length,
		activeRules: active.length
	}
}

/** Held while a tile's source query is still in flight. A `0` there is a claim about
 *  data that has not arrived — the same one the table's empty state used to make — and
 *  the note keeps its own bar so the card does not change height when the figure lands. */
const PendingBar = ({ className }: { className: string }) => (
	<span
		aria-hidden
		className={`block animate-pulse rounded bg-[rgb(var(--cg-surface-2))] ${className}`}
	/>
)

/** An empty prior window means nothing to compare against, so the tile drops the
 *  delta rather than reporting a rise from zero. The arrow carries the sign. */
const versusPrevious = (
	current: number,
	previous: number
): { change?: ReactNode; note?: ReactNode } => {
	if (!previous) return {}
	const percent = Math.round(((current - previous) / previous) * 1000) / 10
	return {
		// `!flex` because this is the one place SwapValue wraps a BOX rather than text:
		// `.custom-rules-swap-value` is `inline-block`, which as a block comes out 4px
		// taller than the chip. The bang is needed — this sheet loads after Tailwind's.
		change: (
			<SwapValue token={percent} className="!flex">
				<Change
					trend={percent < 0 ? 'negative' : 'positive'}
					direction={percent < 0 ? 'down' : 'up'}
					value={`${Math.abs(percent).toFixed(1)}%`}
					size="sm"
				/>
			</SwapValue>
		),
		note: 'vs prev period'
	}
}

export const CustomRulesPage = () => {
	// The header switcher's window; everything on the page is re-read from the same
	// slice. Always starts at 7 days and deliberately does NOT call
	// `useFilterPersistence` — this page never inherits a range picked elsewhere.
	const [days, setDays] = useState<TimeFrame>(INITIAL_TIME_FRAME)

	const asset = useAssetStore((s) => s.asset)
	const demoMode = useAssetStore((s) => s.demoMode)
	// Authorship decides who gets a template's three-dot menu.
	const user = useUserStore((s) => s.user)
	const trackPostHogEvent = useTrackPostHogEvent()
	const entities = useRuleEntities()

	// The route is reachable by URL, so the flag has to gate the PAGE and not just the
	// sidebar row — until the AI Protection cutover a live deep link is a partial
	// release. `useFeatureFlag` returns false while PostHog is still answering as well
	// as when the flag is off, and the provider does not wait, so `flagsReady` is what
	// separates "not allowed" from "not answered yet".
	const customRulesV2Enabled = useFeatureFlag('custom_rules_v2')
	const { flagsReady } = useFeatureFlagsStatus()
	const canView = flagsReady && customRulesV2Enabled

	// The plan boundary the old page draws (`rules-list.tsx:65`): Custom Rules is a
	// Standard+ feature, and the redesign does not move it — the ticket only asks for
	// gating on two CRITERIA, never on the page. A tier this cannot resolve (no
	// subscription, account still loading) reads as allowed, exactly as `!== 'lite'`
	// does today, so nothing flashes a lock screen while the account arrives.
	//
	// Trials excepted, unlike the old page: `hasFeature` and this page's own criteria
	// gating both carry every feature through a trial, and a trial that cannot open the
	// feature is a trial that cannot sell it.
	const accountTier = useAccountStore((s) => s.tier)()
	const isTrial = useAccountStore(
		(s) => s.account?.billing?.subscription?.type === 'TRIAL'
	)
	const planCarriesCustomRules = isTrial || accountTier !== 'lite'

	// Demo and trial workspaces read a seeded asset, as the old page's queries do.
	const accountId = demoMode ? 'workspace-demo' : asset?.account_id
	const assetId = demoMode ? 'website-demo' : asset?.id

	// Asset-wide, so rules created at any level appear here (§7). `select:
	// mapFetchedRules` is deliberately NOT used: Duplicate re-posts a rule's own stored
	// shape, so the raw DTOs have to survive the query.
	const {
		data: fetchedRuleDTOs,
		isFetching: isFetchingRules,
		isLoading: isLoadingRules,
		refetch: refetchRules
	} = useQuery(
		['custom-rules', accountId, assetId],
		() =>
			Rules.listAllRules({ accountId, query: { asset_id: assetId as string } }),
		{ enabled: !!assetId && canView }
	)

	// Newest first is the table's resting order: `GET /rules` returns insertion order,
	// which put a fresh rule — a duplicate especially — last, and past ten rules on a
	// page nobody was looking at. An explicit column sort still overrides this.
	const fetchedRules = useMemo(() => {
		if (!fetchedRuleDTOs) return undefined
		const createdAt = (rule: (typeof fetchedRuleDTOs)[number]) =>
			rule.created_at ? new Date(rule.created_at).getTime() : 0
		return mapFetchedRules(
			[...fetchedRuleDTOs].sort((a, b) => createdAt(b) - createdAt(a))
		)
	}, [fetchedRuleDTOs])

	// TWICE the selected window, so the previous period is a slice of the same series.
	const range = useMemo(() => activityRange(days), [days])
	const {
		data: executionData,
		isFetching: isFetchingExecutions,
		isLoading: isLoadingExecutions,
		refetch: refetchExecutions
	} = useQuery(
		['custom-rules-executions', accountId, assetId, range.from, range.to],
		() =>
			Rules.getRuleExecutions({
				accountId,
				query: {
					asset_id: (assetId as string) || '',
					from: range.from,
					to: range.to
				}
			}),
		{ enabled: !!assetId && canView }
	)

	const activityWindow = useMemo(
		() => buildRuleActivity(executionData, days),
		[executionData, days]
	)

	// Absent until the executions query answers, which keeps a not-yet-loaded column
	// from reading "Never".
	const lastExecutionByRuleId = useMemo(
		() =>
			executionData
				? new Map(
						executionData.rules.map((rule) => [
							rule.ruleId,
							rule.lastExecutedAt
						])
				  )
				: undefined,
		[executionData]
	)

	const fetchedCustomRules = useMemo(
		() =>
			(fetchedRules ?? []).map((rule) =>
				toCustomRule(rule, {
					adAccounts: entities.adAccounts,
					campaigns: entities.campaigns,
					countryNameByCode,
					lastExecutionByRuleId
				})
			),
		[
			fetchedRules,
			entities.adAccounts,
			entities.campaigns,
			lastExecutionByRuleId
		]
	)

	// Local copy for the optimistic toggle. The re-seed below does NOT undo a rejected
	// write: react-query hands back the SAME reference when the server data is unchanged,
	// which is exactly what a rejected write leaves, so the effect never re-runs and the
	// optimistic value sticks. Every mutation rolls its own state back in `catch`.
	const [rules, setRules] = useState<CustomRule[]>([])
	useEffect(() => {
		setRules(applyTimeFrame(fetchedCustomRules, days, activityWindow))
	}, [fetchedCustomRules, days, activityWindow])

	const [chartIds, setChartIds] = useState<Set<string>>(new Set())
	const seededChartIds = useRef(false)
	useEffect(() => {
		// Seed once: re-seeding on every fetch would re-tick boxes the user just cleared.
		if (seededChartIds.current || fetchedCustomRules.length === 0) return
		seededChartIds.current = true
		setChartIds(
			new Set(fetchedCustomRules.slice(0, MAX_CHART_RULES).map((r) => r.id))
		)
	}, [fetchedCustomRules])

	const selectTimeFrame = (frame: TimeFrame) => setDays(frame)

	const refresh = () => {
		refetchRules()
		refetchExecutions()
	}

	// ── Templates (§5) — workspace-scoped; the server rejects a mutation on someone
	//    else's row ─────────────────────────────────────────────────────────────────
	const { data: templateDTOs, refetch: refetchTemplates } = useQuery(
		['custom-rule-templates', accountId],
		() => RuleTemplates.listTemplates({ accountId }),
		{ enabled: !!accountId && canView }
	)

	const templates = useMemo(
		() =>
			(templateDTOs ?? [])
				.map((dto) =>
					toRuleTemplate(dto, user?.id, {
						adAccounts: entities.adAccounts,
						campaigns: entities.campaigns,
						countryNameByCode
					})
				)
				// An unreadable row is skipped, never thrown: this memo maps the whole
				// library, so one bad template used to take the page down with it.
				.filter((template): template is RuleTemplateUI => template !== null),
		[templateDTOs, user?.id, entities.adAccounts, entities.campaigns]
	)

	/** The blueprint is taken from the rule's STORED config — the same source Duplicate
	 *  uses, so a template is a copy without the write mapping. */
	const saveAsTemplate = async (
		ruleId: string,
		name: string,
		scope: TemplateScope
	) => {
		const original = fetchedRuleDTOs?.find((rule) => rule.id === ruleId)
		if (!original || !assetId) return
		try {
			await RuleTemplates.createTemplate({
				accountId,
				payload: {
					asset_id: assetId,
					name,
					description: original.description,
					visibility:
						scope === 'shared'
							? RuleTemplateVisibility.SHARED
							: RuleTemplateVisibility.PRIVATE,
					rule_data: {
						...ruleToTemplateConfig(original),
						name
					}
				}
			})
			trackPostHogEvent(
				PostHogEvent.SAVE_RULE_TEMPLATE,
				PostHogPath.CUSTOM_RULES,
				{ visibility: scope }
			)
		} catch {
			toast.error('Failed to save the template')
		} finally {
			refetchTemplates()
		}
	}

	const toggleTemplateVisibility = async (template: RuleTemplateUI) => {
		try {
			await RuleTemplates.setTemplateVisibility({
				accountId,
				templateId: template.id,
				visibility:
					template.scope === 'shared'
						? RuleTemplateVisibility.PRIVATE
						: RuleTemplateVisibility.SHARED
			})
		} catch {
			toast.error('Failed to change who can use this template')
		} finally {
			refetchTemplates()
		}
	}

	/** Tray "Edit" → the sheet → Save. Writes back to the template and NOWHERE else:
	 *  rules already created from it keep the configuration they were created with. */
	const saveTemplate = async (templateId: string, draft: RuleDraft) => {
		if (!assetId) return
		try {
			await RuleTemplates.updateTemplate({
				accountId,
				templateId,
				payload: {
					// The scope was picked from THIS website's entities, so this is the
					// site the saved ids belong to. Sending it keeps `asset_id` and
					// `rule_data.scope` describing the same place.
					asset_id: assetId,
					name: draft.name,
					description: draft.description,
					rule_data: prepareRuleData(draft, { entities, countryCodeByName })
				}
			})
		} catch (error) {
			toast.error('Failed to save the template')
			// Rethrown so the sheet stays open on the edit that was refused.
			throw error
		} finally {
			refetchTemplates()
		}
	}

	const deleteTemplate = async (template: RuleTemplateUI) => {
		try {
			await RuleTemplates.deleteTemplate({
				accountId,
				templateId: template.id
			})
		} catch {
			toast.error('Failed to delete the template')
		} finally {
			refetchTemplates()
		}
	}

	// ── Mutations ──
	//
	// Every one of these reports its own failure and puts back what it changed. They used
	// to be `try/finally` with no `catch`: a 400 or 403 closed the drawer, dropped the
	// edit and said nothing (the old page toasts on the same paths,
	// `rules-list.tsx:111`). The two that feed the sheet RETHROW as well, so it can stay
	// open holding the user's draft instead of closing over a failed save.

	/** Create and edit (plan §2.2): both post the same `rule_data`, only the endpoint
	 *  differs. No optimistic write, unlike the toggle — a saved rule comes back with
	 *  fields the drawer never had.
	 *
	 *  KNOWN DEVIATION, raised on the ticket. A rule saved here names its targets in
	 *  `scope.values` with both id columns null, which the engine matches through a
	 *  fourth bucket that is NOT gated on `level` (`core/lib/utils/protection.ts:185`,
	 *  reasoning at `:113-123`) — unlike the ad-account and website buckets (`:165`,
	 *  `:175`), which `rules_settings.inherit` can switch off.
	 *
	 *  So a campaign with `inherit: false` ignores an OLD-page rule scoped to its ad
	 *  account but still receives a NEW-page one scoped to the same account: two rules
	 *  that read alike behave differently by origin. Deliberate — a rule naming a target
	 *  directly never cascaded, and the campaign bucket (`:156`) has never been gated
	 *  either — and additive, since the old page always pairs `ownership: AD_ACCOUNT`
	 *  with a real `ad_account_id` (`rule-form.tsx:89`). */
	const saveRule = async (draft: RuleDraft, ruleId?: string) => {
		if (!assetId) return
		const rule_data = prepareRuleData(draft, {
			entities,
			countryCodeByName
		})
		try {
			if (ruleId) {
				await Rules.updateCustomRule({
					accountId,
					ruleId,
					payload: {
						rule_data,
						// Editing can re-scope a rule, so the targeting is rewritten
						// with it — including nulling the id columns on an OLD-page rule.
						...NEW_MODEL_ID_COLUMNS
					}
				})
			} else {
				await Rules.createCustomRule({
					accountId,
					payload: {
						asset_id: assetId,
						// Both null on purpose — the marker of a new-model rule.
						...NEW_MODEL_ID_COLUMNS,
						rule_data
					}
				})
				trackPostHogEvent(
					PostHogEvent.ADD_CUSTOM_RULE,
					PostHogPath.CUSTOM_RULES,
					{ source: 'drawer', scope: draft.scope.mode }
				)
			}
		} catch (error) {
			toast.error(
				ruleId ? 'Failed to save the rule' : 'Failed to create the rule'
			)
			// Rethrown so the sheet stays open holding the draft — closing over a refused
			// save is how the user's edit disappeared with no message.
			throw error
		} finally {
			refresh()
		}
	}

	const toggleRuleStatus = async (ruleId: string, nextActive: boolean) => {
		// Captured before the optimistic write, and restored below: the refetch cannot do
		// it (see the note on `rules`), so without this the row keeps showing a state the
		// server refused.
		const previousRules = rules
		setRules((current) =>
			current.map((rule) =>
				rule.id === ruleId
					? { ...rule, status: nextActive ? 'active' : 'paused' }
					: rule
			)
		)
		try {
			await Rules.toggleRuleStatus({
				accountId: accountId as string,
				id: ruleId,
				enabled: nextActive
			})
		} catch {
			setRules(previousRules)
			toast.error('Failed to toggle rule status')
		} finally {
			refetchRules()
		}
	}

	const deleteRule = async (ruleId: string) => {
		const previousRules = rules
		setRules((current) => current.filter((rule) => rule.id !== ruleId))
		try {
			await Rules.deleteRule({ accountId: accountId as string, ruleId })
		} catch {
			setRules(previousRules)
			toast.error('Failed to delete the rule')
		} finally {
			refresh()
		}
	}

	/** Duplicate re-posts the ORIGINAL rule's stored shape, changing only the name and
	 *  the status, so it stays clear of the write mapping. Paused in the SAME call:
	 *  `createRule` reads `ruleData.status`, and creating it running to disable it after
	 *  left a live copy behind whenever the second call failed. */
	const duplicateRule = async (ruleId: string) => {
		const original = fetchedRuleDTOs?.find((rule) => rule.id === ruleId)
		if (!original) return
		try {
			await Rules.createCustomRule({
				accountId,
				payload: {
					asset_id: original.asset_id,
					ad_account_id: original.ad_account_id,
					campaign_id: original.campaign_id,
					rule_data: {
						name: `${original.name} (copy)`,
						status: RuleStatus.PAUSED,
						description: original.description,
						ownership: original.ownership,
						resolve_status: original.resolve_status,
						focus: original.focus,
						scope: original.scope,
						actions: original.actions,
						criteria: original.criteria,
						type: original.type
					}
				}
			})
			// The existing rule-creation event, so the count stays comparable with the old page.
			trackPostHogEvent(
				PostHogEvent.ADD_CUSTOM_RULE,
				PostHogPath.CUSTOM_RULES,
				{
					source: 'duplicate'
				}
			)
		} catch {
			toast.error('Failed to duplicate the rule')
		} finally {
			refresh()
		}
	}

	const selectedRules = rules.filter((rule) => chartIds.has(rule.id))
	const kpis = summarize(rules, days, activityWindow)
	// `isFetching`, not `isLoading`: the latter is only true before the first result.
	const isFetching = isFetchingRules || isFetchingExecutions
	// Both counters are summed per rule, so either query still landing leaves them wrong.
	const isLoadingWindow = isLoadingRules || isLoadingExecutions

	useFadingScrollbar()

	// Marks the body while this page is mounted, so custom-rules-page.css can lift the
	// HubSpot launcher HERE without changing its stacking elsewhere.
	useEffect(() => {
		document.body.setAttribute('data-custom-rules-page', '')
		return () => document.body.removeAttribute('data-custom-rules-page')
	}, [])

	// Divider appears only once the header is stuck — PageLayout's sentinel recipe.
	const sentinelRef = useRef<HTMLDivElement>(null)
	const [isStuck, setIsStuck] = useState(false)

	useEffect(() => {
		const sentinel = sentinelRef.current
		if (!sentinel || typeof IntersectionObserver === 'undefined') return
		const observer = new IntersectionObserver(
			([entry]) => setIsStuck(!entry.isIntersecting),
			{ threshold: 0 }
		)
		observer.observe(sentinel)
		return () => observer.disconnect()
	}, [])

	// Below every hook on purpose: returning earlier would change the hook order.
	// `null` rather than a redirect while the flags land — bouncing someone who DOES
	// have the flag is worse than a blank frame.
	if (!flagsReady) return null
	if (!customRulesV2Enabled) return <Navigate to=".." replace />
	// NOT a redirect like the flag above, and the difference is the point: the flag says
	// the page does not exist yet, while a plan that does not carry Custom Rules is the
	// upsell moment. The old page shows the upgrade panel rather than bouncing, and that
	// screen is the only route a lite workspace has to the plan that unlocks this.
	if (!planCarriesCustomRules) return <CustomRulesPlanLocked />

	return (
		<RuleActivityProvider value={activityWindow}>
			<PageTitle pageName="Custom Rules" />
			<Surface
				bg="canvas"
				radius="none"
				bordered={false}
				className="cg-page-surface min-h-full"
			>
				{/* Four tall: the distance the page travels before the header is stuck. The
				    negative margin cancels the height. */}
				<div ref={sentinelRef} aria-hidden="true" className="-mb-1 h-1" />
				{/* 16px of headroom at rest, 12px pinned: one sticky offset, not a scroll
				    listener. The strip carries the full 16px and pins at -4. */}
				<div
					className={`cg-page-surface cg-header-bleed sticky -top-1 z-20 border-b transition-colors ${
						isStuck ? 'border-[rgb(var(--cg-border))]' : 'border-transparent'
					}`}
				>
					<div className="px-6 pb-3 pt-4">
						<PageHeader
							layout="inline"
							title="Custom Rules"
							subtitle="Fine-tune your protection with rules built around your needs."
							actions={
								<SegmentedTabs
									value={String(days)}
									onChange={(value) =>
										selectTimeFrame(Number(value) as TimeFrame)
									}
									size="small"
								>
									<SegmentedTabList>
										{TIME_FRAMES.map((frame) => (
											<SegmentedTab
												key={frame}
												value={String(frame)}
												label={`${frame} days`}
											/>
										))}
									</SegmentedTabList>
								</SegmentedTabs>
							}
						/>
					</div>
				</div>

				<div className="flex flex-col gap-4 p-6 pt-4">
					<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
						{/* The first two counts sit still through a 30/7 switch — they are read off
						    rule actions and status, which the window does not touch. */}
						<Card radius="xl" padding="compact">
							<MetricTile
								icon={<Shield01 />}
								label="Active rules"
								value={
									isLoadingRules ? (
										<PendingBar className="my-1 h-6 w-10" />
									) : (
										<SwapValue token={kpis.activeRules}>
											{kpis.activeRules.toString()}
										</SwapValue>
									)
								}
								note={
									isLoadingRules ? (
										<PendingBar className="h-3 w-20" />
									) : (
										`of ${rules.length} rule${rules.length === 1 ? '' : 's'}`
									)
								}
							/>
						</Card>
						<Card radius="xl" padding="compact">
							<MetricTile
								icon={<Lock01 />}
								label="Protective rules"
								info="Active rules that blacklist or exclude the source. The rest only label the click and let it run."
								infoMaxWidth={KPI_INFO_WIDTH}
								value={
									isLoadingRules ? (
										<PendingBar className="my-1 h-6 w-10" />
									) : (
										<SwapValue token={kpis.protectiveRules}>
											{kpis.protectiveRules.toString()}
										</SwapValue>
									)
								}
								note={
									isLoadingRules ? (
										<PendingBar className="h-3 w-24" />
									) : (
										`of ${kpis.activeRules} active rule${
											kpis.activeRules === 1 ? '' : 's'
										}`
									)
								}
							/>
						</Card>
						<Card radius="xl" padding="compact">
							<MetricTile
								icon={<ShieldZap />}
								label="Executions"
								info="How many times your custom rules matched traffic in the selected period."
								infoMaxWidth={KPI_INFO_WIDTH}
								value={
									isLoadingWindow ? (
										<PendingBar className="my-1 h-6 w-16" />
									) : (
										<SwapValue token={kpis.executions}>
											{kpis.executions.toLocaleString()}
										</SwapValue>
									)
								}
								{...(isLoadingWindow
									? { note: <PendingBar className="h-3 w-24" /> }
									: versusPrevious(kpis.executions, kpis.previous.executions))}
							/>
						</Card>
						<Card radius="xl" padding="compact">
							<MetricTile
								icon={<SlashOctagon />}
								label="Blocked sources"
								info="Protective actions created from those rule executions."
								infoMaxWidth={KPI_INFO_WIDTH}
								value={
									isLoadingWindow ? (
										<PendingBar className="my-1 h-6 w-16" />
									) : (
										<SwapValue token={kpis.blocked}>
											{kpis.blocked.toLocaleString()}
										</SwapValue>
									)
								}
								{...(isLoadingWindow
									? { note: <PendingBar className="h-3 w-24" /> }
									: versusPrevious(kpis.blocked, kpis.previous.blocked))}
							/>
						</Card>
					</div>

					<CustomRulesTable
						rules={rules}
						onSaveRule={saveRule}
						chartIds={chartIds}
						onChartIdsChange={setChartIds}
						days={days}
						onRefresh={refresh}
						isRefreshing={isFetching}
						isLoading={isLoadingRules}
						onToggleRule={toggleRuleStatus}
						onDeleteRule={deleteRule}
						onDuplicateRule={duplicateRule}
						templates={templates}
						onSaveAsTemplate={saveAsTemplate}
						onToggleTemplateVisibility={toggleTemplateVisibility}
						onDeleteTemplate={deleteTemplate}
						onSaveTemplate={saveTemplate}
					/>

					<RulesActivityChart selectedRules={selectedRules} days={days} />
				</div>
			</Surface>
		</RuleActivityProvider>
	)
}
